import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { reportsAPI } from '../utils/api';
import { mergeDashboardResponses } from '../utils/report/mergeNetworkReports';
import { APP_TIMEZONE, nowTimeInTZ, todayInTZ } from '../utils/datetime';
import TimezoneSwitcher from '../components/ui/TimezoneSwitcher';
import useReportTimezone from '../hooks/useReportTimezone';
import {
  getDateRestriction,
  clampDateRange,
  clampPresetRange,
  clampDateValue,
  initialReportDatesForUser,
  defaultReportRangeForUser,
  formatDateRestrictionLabel,
  isPresetAllowedForRestriction,
  isFixedDateRestriction,
  isCustomRangeIncomplete,
} from '../utils/dateRestriction';
import AccessRestricted from '../components/ui/AccessRestricted';
import MultiSelect from '../components/ui/MultiSelect';
import FilterChips from '../components/ui/FilterChips';
import ReportBuilderFilters from '../components/ui/ReportBuilderFilters';
import ReportSettingsFilters from '../components/ui/ReportSettingsFilters';
import PageHeader from '../components/ui/PageHeader';
import {
  dimensionsToChips,
  metricsToChips,
  reportSettingsToChips,
  dimensionLabel,
  metricLabel,
  DEFAULT_REPORT_DIMENSIONS,
  DEFAULT_REPORT_METRICS,
  DEFAULT_REPORT_SETTINGS,
} from '../utils/report/gamReportCatalog';
import { buildFilterDropdownOptions } from '../utils/catalogOptions';
import { normalizeInventorySelections, slimFiltersForPersist, isAllSelection, ALL_SENTINEL } from '../utils/inventorySelection';
import { buildAppliedFilterChips, removeFilterChip } from '../utils/filterChips';
import { saveReportPage } from '../store/slices/reportSlice';
import { isReportCacheFresh } from '../hooks/useReportPageCache';
import { useMedia } from '../hooks/useMedia';
import DynamicReportTable from '../components/ui/DynamicReportTable';
import ReportAutoCharts from '../components/ui/ReportAutoCharts';
import GamReportControlBar from '../components/ui/GamReportControlBar';
import {
  resolveReportTableConfig,
  buildReportColumns,
  hasActiveReport,
  aggregateRowsByColumns,
  summarizeReportingRows,
} from '../utils/dynamicReportTable';
import { enrichReportRows, sortRowsByCompleteness } from '../utils/enrichReportRows';
import { resolveReportingQuery } from '../utils/reportSelection';
import { rowMatchesChartDimension } from '../utils/reportChartSuggest';
import { useReportHotkeys } from '../hooks/useReportHotkeys';
import { showToast } from '../hooks/useToast';
import { encodeReportShare, parseReportShare, copyReportLink } from '../utils/reportShare';
import { usePermissions } from '../hooks/usePermissions';
import { NO_VIEW_REPORTS_MSG, NO_VIEW_REPORTS_TITLE, getAssignedInventoryScope, hasAssignedInventory, isAdmin } from '../utils/permissions';
import {
  EMPTY_INVENTORY_FILTERS,
  draftHasInventorySelection,
  getAssignedFilterVisibility,
  initialInventoryDraft,
  shouldAutoLoadScopedInventory,
  buildAssignedInventoryFilters,
} from '../utils/assignedInventoryFilters';
import { getUserFacingMessage, logErrorForDebug } from '../utils/userFacingError';
import NoDomainsAssignedNote from '../components/ui/NoDomainsAssignedNote';
import { getRecentFilters, saveRecentFilter, applyRecentFilter, clearRecentFilters, RECENT_FILTERS_CLEARED_EVENT } from '../utils/recentFilters';
import SavedFiltersBar from '../components/ui/SavedFiltersBar';
import SavePresetButton from '../components/ui/SavePresetButton';
import DataFreshness from '../components/ui/DataFreshness';
import { SAVED_FILTERS_PAGES, getSavedFilters } from '../utils/savedFilters';
import { PRESET_PAGES, filtersOnlySnapshot } from '../utils/reportPresets';
import { getLastPageFilters, saveLastPageFilters, clearLastPageFilters, LAST_FILTER_PAGES } from '../utils/lastPageFilters';
import { downloadCsv, downloadExcel, exportCellValue } from '../utils/tableExport';

const PAGE_SIZE = 50;
const POLL_MS = 30 * 60 * 1000; // matches backend 30-min cache TTL
const BUILDING_POLL_MAX_TRIES = 36; // 36 × 5s ≈ 3 min before we call it empty

function reportingAppliedKey(applied = {}) {
  const list = (v) => (Array.isArray(v) ? v : (v != null && v !== '' ? [v] : []))
    .map((x) => String(x).trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join(',');
  const dims = (applied.reportDimensions || []).map(String).sort().join(',');
  const mets = (applied.reportMetrics || []).map(String).sort().join(',');
  return [
    String(applied.startDate || '').slice(0, 10),
    String(applied.endDate || '').slice(0, 10),
    list(applied.domain),
    list(applied.site),
    list(applied.domainName),
    list(applied.domainId),
    list(applied.country),
    dims,
    mets,
  ].join('|');
}

function reportPayloadHasRows(payload) {
  if (!payload) return false;
  if (Array.isArray(payload.rows) && payload.rows.length > 0) return true;
  const s = payload.summary || {};
  return (Number(s.totalRevenue) || 0) > 0
    || (Number(s.offeredRecords) || 0) > 0
    || (Number(s.impressions) || 0) > 0
    || (Number(s.revenue) || 0) > 0;
}

const MET_REVENUE = 'total_line_item_level_cpm_and_cpc_revenue';
const MET_IMPRESSIONS = 'total_line_item_level_impressions';
const MET_CTR = 'total_line_item_level_ctr';
const MET_CLICKS = 'total_line_item_level_clicks';
const MET_FILL = 'total_fill_rate';

/** One-click report presets (dims/metrics + optional date preset). */
const QUICK_VIEWS = [
  { id: 'today', label: 'Today', preset: 'today' },
  {
    id: 'rev-domain',
    label: 'Revenue by domain',
    preset: 'last7',
    dims: ['date', 'domain'],
    mets: [MET_REVENUE, MET_IMPRESSIONS],
  },
  {
    id: 'fill',
    label: 'Fill rate',
    preset: 'last7',
    dims: ['date'],
    mets: [MET_FILL, MET_IMPRESSIONS],
  },
  {
    id: 'ctr',
    label: 'CTR trend',
    preset: 'last7',
    dims: ['date'],
    mets: [MET_CTR, MET_CLICKS, MET_IMPRESSIONS],
  },
];

function normalizeCountry(v) {
  if (v == null || v === '') return [];
  return Array.isArray(v) ? v.filter(Boolean) : [v];
}

// Parent domain from an ad-unit name, e.g. "arenahubply.com_inter (233...)"
// → "arenahubply.com". Keep this in sync with the backend (reports.js).

function money(v, currency = 'USD') {
  const sym = currency === 'INR' ? '\u20B9' : '$';
  const num = parseFloat(v || 0);
  return `${sym}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function num(v) {
  return parseInt(v || 0).toLocaleString();
}

function ReportingView() {
  const dispatch = useDispatch();
  const { has, visibility: clientVis, user } = usePermissions();
  const canGenerate = has('canGenerateReports');
  const inventoryScope = getAssignedInventoryScope(user);
  const inventoryAssigned = hasAssignedInventory(user);
  const filterVisibility = getAssignedFilterVisibility(user);
  const outlet = useOutletContext() || {};
  const { networkInfo } = outlet;
  const accountNetworks = Array.isArray(outlet.accountNetworks) ? outlet.accountNetworks : [];
  const mergeIds = useMemo(() => {
    if (isAdmin(user)) return null;
    const ids = (Array.isArray(outlet.mergeNetworkIds) && outlet.mergeNetworkIds.length
      ? outlet.mergeNetworkIds
      : accountNetworks.map((n) => n.id)
    ).map((id) => String(id || '').trim()).filter(Boolean);
    return ids.length > 1 ? [...new Set(ids)] : null;
  }, [user, outlet.mergeNetworkIds, accountNetworks]);
  const mergeCacheKey = mergeIds ? `merge:${[...mergeIds].sort().join(',')}` : null;
  // Admin: sidebar network. Domain multi-network: merge key. Else primary client.
  const reportClientId = mergeCacheKey
    || outlet.viewClientId
    || user?.clientId
    || null;
  const reportCacheRef = useRef(new Map());

  const scopedNormOpts = useMemo(() => ({
    expandAll: !!filterVisibility.isScopedUser,
    assignedScope: inventoryScope,
  }), [filterVisibility.isScopedUser, inventoryScope]);
  const savedRaw = useSelector((s) => s.reports?.reporting);
  const saved = (
    (!savedRaw?.userId || savedRaw.userId === user?.id)
    && user?.clientId
    && savedRaw?.clientId
    && String(savedRaw.clientId) === String(user.clientId)
  ) ? savedRaw : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const shareHydratedRef = useRef(false);
  const undoSnapRef = useRef(null);
  const dateRestriction = useMemo(() => getDateRestriction(user), [user]);
  const dateFilterLocked = Boolean(isFixedDateRestriction(dateRestriction));
  const todayInit = useMemo(() => defaultReportRangeForUser(user), [user]);
  const initDates = useMemo(() => initialReportDatesForUser(user, saved), [user, saved]);
  const buildDefaultApplied = () => ({
    ...todayInit,
    country: [],
    ...EMPTY_INVENTORY_FILTERS,
    reportDimensions: [...DEFAULT_REPORT_DIMENSIONS],
    // Total revenue (+ impressions) by default so cards load without Apply.
    reportMetrics: [...DEFAULT_REPORT_METRICS],
    reportSettings: DEFAULT_REPORT_SETTINGS,
  });
  const buildScopedApplied = (dates = todayInit) => ({
    ...buildDefaultApplied(),
    ...dates,
    ...buildAssignedInventoryFilters(user),
  });
  const cacheFresh = isReportCacheFresh(saved, POLL_MS, { clientId: user?.clientId })
    && Boolean(resolveReportingQuery(saved?.applied));

  const savedInv = saved ? {
    domain: saved.domain, site: saved.site, domainName: saved.domainName, domainId: saved.domainId,
  } : {};
  const invDraft = initialInventoryDraft(user, savedInv);
  const scopedAutoLoad = shouldAutoLoadScopedInventory(user);

  const [preset, setPreset] = useState(() => saved?.preset ?? 'today');
  const [startDate, setStartDate] = useState(() => initDates.startDate);
  const [endDate, setEndDate] = useState(() => initDates.endDate);
  const [country, setCountry] = useState(() => normalizeCountry(saved?.country));
  const [domainName, setDomainName] = useState(() => (
    filterVisibility.isScopedUser ? invDraft.domainName : (saved?.domainName ?? invDraft.domainName)
  ));
  const [domainId, setDomainId] = useState(() => (
    filterVisibility.isScopedUser ? invDraft.domainId : (saved?.domainId ?? invDraft.domainId)
  ));
  const [domain, setDomain] = useState(() => (
    filterVisibility.isScopedUser ? invDraft.domain : (saved?.domain ?? invDraft.domain)
  ));
  const [site, setSite] = useState(() => (
    filterVisibility.isScopedUser ? invDraft.site : (saved?.site ?? invDraft.site)
  ));
  const [catalog, setCatalog] = useState([]);
  const [catalogLists, setCatalogLists] = useState({ domainRoots: [], siteHosts: [], sitesByDomain: {}, adUnitsByHost: {}, appIds: [] });
  const [noDomainsAssigned, setNoDomainsAssigned] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryLoading, setCountryLoading] = useState(true);
  const countryMultiOptions = useMemo(
    () => countryOptions.map((c) => ({ value: String(c.id), label: c.name })),
    [countryOptions]
  );

  const [filtersOpen, setFiltersOpen] = useState(() => saved?.filtersOpen ?? !filterVisibility.isScopedUser);
  const [breakdownOpen, setBreakdownOpen] = useState(() => saved?.breakdownOpen ?? true);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  // Restore saved builder state; default metrics = Total revenue (+ impressions).
  const [reportDimensions, setReportDimensions] = useState(() => (
    saved?.reportDimensions?.length ? saved.reportDimensions : [...DEFAULT_REPORT_DIMENSIONS]
  ));
  const [reportMetrics, setReportMetrics] = useState(
    () => (saved?.reportMetrics?.length ? saved.reportMetrics : [...DEFAULT_REPORT_METRICS])
  );
  const [reportSettings, setReportSettings] = useState(
    () => saved?.reportSettings ?? DEFAULT_REPORT_SETTINGS
  );
  const filterPanelRef = useRef(null);

  const [applied, setApplied] = useState(() => {
    const withCountry = (obj) => ({ ...obj, country: normalizeCountry(obj?.country) });
    const ensureDefaults = (obj) => {
      const o = withCountry(obj);
      const next = { ...o };
      if (!next.reportMetrics?.length) next.reportMetrics = [...DEFAULT_REPORT_METRICS];
      if (!next.reportDimensions?.length) next.reportDimensions = [...DEFAULT_REPORT_DIMENSIONS];
      if (!next.reportSettings || typeof next.reportSettings !== 'object') {
        next.reportSettings = { ...DEFAULT_REPORT_SETTINGS };
      } else if (!next.reportSettings.runType) {
        next.reportSettings = { ...DEFAULT_REPORT_SETTINGS, ...next.reportSettings };
      }
      return next;
    };
    // Domain user: never restore auto-applied full inventory — dates + default metrics only.
    if (filterVisibility.isScopedUser) {
      return ensureDefaults({
        ...buildDefaultApplied(),
        startDate: saved?.applied?.startDate || saved?.startDate || todayInit.startDate,
        endDate: saved?.applied?.endDate || saved?.endDate || todayInit.endDate,
        ...EMPTY_INVENTORY_FILTERS,
      });
    }
    if (saved?.applied) return ensureDefaults(saved.applied);
    if (scopedAutoLoad) return ensureDefaults(buildScopedApplied());
    return ensureDefaults(buildDefaultApplied());
  });
  const [data, setData] = useState(() => (cacheFresh ? saved?.data : null) ?? null);
  // Nothing loads or shows until the user clicks Apply Filter. The only
  // exception is a fresh cache from a previous session, which we restore.
  const [hasApplied, setHasApplied] = useState(
    () => Boolean(cacheFresh && (saved?.data || saved?.progData))
  );
  const [loading, setLoading] = useState(() => !Boolean(cacheFresh && (saved?.data || saved?.progData)));
  const [slowLoad, setSlowLoad] = useState(false);
  const [recentFilters, setRecentFilters] = useState(() => getRecentFilters(user?.id));
  const slowTimerRef = useRef(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(() => saved?.page ?? 1);
  const [search, setSearch] = useState(() => saved?.search ?? '');
  const [segmentFilter, setSegmentFilter] = useState(null);
  const [activeQuickView, setActiveQuickView] = useState(null);
  const [tableDensity, setTableDensity] = useState(() => {
    try {
      return localStorage.getItem('adnexus.tableDensity:reporting') === 'compact' ? 'compact' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });
  const [lastUpdated, setLastUpdated] = useState(() => saved?.lastUpdated ?? null);
  const [fetchedAt, setFetchedAt] = useState(() => saved?.fetchedAt ?? null);
  const [progData, setProgData] = useState(() => (cacheFresh ? saved?.progData : null) ?? null);
  const pollRef = useRef(null);
  const skipInitialLoadRef = useRef(cacheFresh);
  const loadGenRef = useRef(0);
  const buildingPollRef = useRef(0);
  const loadInFlightRef = useRef(false);
  const loadKeyRef = useRef('');
  const dataRef = useRef(null);
  const progDataRef = useRef(null);
  // Set when the background report never finished — stops the endless hourglass.
  const [buildingTimedOut, setBuildingTimedOut] = useState(false);

  useEffect(() => {
    setRecentFilters(getRecentFilters(user?.id));
  }, [user?.id]);

  useEffect(() => {
    try {
      localStorage.setItem('adnexus.tableDensity:reporting', tableDensity);
    } catch {
      /* ignore */
    }
  }, [tableDensity]);

  useEffect(() => {
    const onCleared = () => setRecentFilters([]);
    window.addEventListener(RECENT_FILTERS_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(RECENT_FILTERS_CLEARED_EVENT, onCleared);
  }, []);

  useEffect(() => {
    if (shareHydratedRef.current) return;
    shareHydratedRef.current = true;
    const viewId = searchParams.get('view');
    if (viewId) {
      const found = getSavedFilters(SAVED_FILTERS_PAGES.reporting, user?.id).find((f) => f.id === viewId);
      if (found?.snapshot) {
        const snap = found.snapshot;
        if (snap.domain) setDomain(snap.domain);
        if (snap.site) setSite(snap.site);
        if (snap.domainName) setDomainName(snap.domainName);
        if (snap.domainId) setDomainId(snap.domainId);
        if (snap.country) setCountry(snap.country);
        if (snap.reportDimensions) setReportDimensions(snap.reportDimensions);
        if (snap.reportMetrics) setReportMetrics(snap.reportMetrics);
        if (snap.reportSettings) setReportSettings(snap.reportSettings);
      }
    }
    const shared = parseReportShare(searchParams);
    if (shared) {
      if (shared.preset && shared.preset !== 'custom') {
        const r = clampPresetRange(shared.preset, dateRestriction);
        setPreset(shared.preset);
        setStartDate(r.startDate);
        setEndDate(r.endDate);
        if (shared.reportDimensions?.length) setReportDimensions(shared.reportDimensions);
        if (shared.reportMetrics?.length) setReportMetrics(shared.reportMetrics);
        if (shared.domain?.length) setDomain(shared.domain);
        if (shared.site?.length) setSite(shared.site);
        if (shared.domainName?.length) setDomainName(shared.domainName);
        if (shared.domainId?.length) setDomainId(shared.domainId);
        if (shared.country?.length) setCountry(shared.country);
        setHasApplied(true);
        setApplied((prev) => ({
          ...prev,
          ...r,
          domain: shared.domain?.length ? shared.domain : prev.domain,
          site: shared.site?.length ? shared.site : prev.site,
          domainName: shared.domainName?.length ? shared.domainName : prev.domainName,
          domainId: shared.domainId?.length ? shared.domainId : prev.domainId,
          country: shared.country?.length ? shared.country : prev.country,
          reportDimensions: shared.reportDimensions?.length ? shared.reportDimensions : prev.reportDimensions,
          reportMetrics: shared.reportMetrics?.length ? shared.reportMetrics : prev.reportMetrics,
        }));
        return;
      }
      if (shared.startDate && shared.endDate) {
        const r = clampDateRange(shared.startDate, shared.endDate, dateRestriction);
        setPreset(shared.preset || 'custom');
        setStartDate(r.startDate);
        setEndDate(r.endDate);
        if (shared.reportDimensions?.length) setReportDimensions(shared.reportDimensions);
        if (shared.reportMetrics?.length) setReportMetrics(shared.reportMetrics);
        if (shared.domain?.length) setDomain(shared.domain);
        if (shared.site?.length) setSite(shared.site);
        if (shared.domainName?.length) setDomainName(shared.domainName);
        if (shared.domainId?.length) setDomainId(shared.domainId);
        if (shared.country?.length) setCountry(shared.country);
        setHasApplied(true);
        setApplied((prev) => ({
          ...prev,
          ...r,
          domain: shared.domain?.length ? shared.domain : prev.domain,
          site: shared.site?.length ? shared.site : prev.site,
          domainName: shared.domainName?.length ? shared.domainName : prev.domainName,
          domainId: shared.domainId?.length ? shared.domainId : prev.domainId,
          country: shared.country?.length ? shared.country : prev.country,
          reportDimensions: shared.reportDimensions?.length ? shared.reportDimensions : prev.reportDimensions,
          reportMetrics: shared.reportMetrics?.length ? shared.reportMetrics : prev.reportMetrics,
        }));
      }
      return;
    }
    if (viewId) return;
    const last = getLastPageFilters(LAST_FILTER_PAGES.reporting, user?.id);
    if (!last?.startDate || !last?.endDate) return;
    const r = clampDateRange(last.startDate, last.endDate, dateRestriction);
    setPreset(last.preset || 'custom');
    setStartDate(r.startDate);
    setEndDate(r.endDate);
    if (last.domain?.length) setDomain(last.domain);
    if (last.site?.length) setSite(last.site);
    if (last.domainName?.length) setDomainName(last.domainName);
    if (last.domainId?.length) setDomainId(last.domainId);
    if (last.country?.length) setCountry(last.country);
    if (last.reportDimensions?.length) setReportDimensions(last.reportDimensions);
    if (last.reportMetrics?.length) setReportMetrics(last.reportMetrics);
    if (last.reportSettings && Object.keys(last.reportSettings).length) {
      setReportSettings(last.reportSettings);
    }
    const nextApplied = {
      ...r,
      domain: last.domain?.length ? last.domain : [],
      site: last.site?.length ? last.site : [],
      domainName: last.domainName?.length ? last.domainName : [],
      domainId: last.domainId?.length ? last.domainId : [],
      country: last.country?.length ? last.country : [],
      reportDimensions: last.reportDimensions?.length
        ? last.reportDimensions
        : [...DEFAULT_REPORT_DIMENSIONS],
      reportMetrics: last.reportMetrics?.length
        ? last.reportMetrics
        : [...DEFAULT_REPORT_METRICS],
      reportSettings: last.reportSettings && Object.keys(last.reportSettings).length
        ? last.reportSettings
        : DEFAULT_REPORT_SETTINGS,
    };
    setApplied((prev) => ({ ...prev, ...nextApplied }));
    setHasApplied(true);
    skipInitialLoadRef.current = false;
  }, [searchParams, user?.id, dateRestriction]);

  useEffect(() => {
    if (!dateRestriction) return;
    if (dateFilterLocked) {
      setStartDate(dateRestriction.startDate);
      setEndDate(dateRestriction.endDate);
      return;
    }
    if (!isCustomRangeIncomplete(preset, startDate, endDate)) {
      const r = clampDateRange(startDate, endDate, dateRestriction);
      if (r.startDate !== startDate || r.endDate !== endDate) {
        setStartDate(r.startDate);
        setEndDate(r.endDate);
      }
    }
    if (!isPresetAllowedForRestriction(preset, dateRestriction) && preset !== 'custom') {
      setPreset('custom');
    }
  }, [dateRestriction, dateFilterLocked]);

  const currency = data?.summary?.currency || networkInfo?.currencyCode || 'USD';
  const isNarrow = useMedia('(max-width: 768px)');

  const persistRecentFilter = useCallback(() => {
    const snapshot = {
      preset,
      startDate,
      endDate,
      country,
      domain,
      site,
      domainName,
      domainId,
      reportDimensions,
      reportMetrics,
      reportSettings,
    };
    setRecentFilters(saveRecentFilter(slimFiltersForPersist(snapshot), user?.id));
  }, [preset, startDate, endDate, country, domain, site, domainName, domainId, reportDimensions, reportMetrics, reportSettings, user?.id]);

  const getSavedFilterSnapshot = useCallback(() => slimFiltersForPersist({
    country,
    domain,
    site,
    domainName,
    domainId,
    reportDimensions,
    reportMetrics,
    reportSettings,
  }), [country, domain, site, domainName, domainId, reportDimensions, reportMetrics, reportSettings]);

  const getPresetSnapshot = useCallback(() => filtersOnlySnapshot({
    country: applied?.country || country,
    domain: applied?.domain || domain,
    site: applied?.site || site,
    domainName: applied?.domainName || domainName,
    domainId: applied?.domainId || domainId,
    reportDimensions: applied?.reportDimensions || reportDimensions,
    reportMetrics: applied?.reportMetrics || reportMetrics,
  }), [
    applied, country, domain, site, domainName, domainId, reportDimensions, reportMetrics,
  ]);

  const handleApplySavedFilter = useCallback((snapshot) => {
    const nextCountry = normalizeCountry(snapshot.country);
    const nextDomain = snapshot.domain || [];
    const nextSite = snapshot.site || [];
    const nextDomainName = snapshot.domainName || [];
    const nextDomainId = snapshot.domainId || [];
    const nextDims = snapshot.reportDimensions?.length
      ? snapshot.reportDimensions
      : DEFAULT_REPORT_DIMENSIONS;
    const nextMets = snapshot.reportMetrics?.length
      ? snapshot.reportMetrics
      : DEFAULT_REPORT_METRICS;
    const nextSettings = snapshot.reportSettings && Object.keys(snapshot.reportSettings).length
      ? snapshot.reportSettings
      : DEFAULT_REPORT_SETTINGS;

    setCountry(nextCountry);
    setDomain(nextDomain);
    setSite(nextSite);
    setDomainName(nextDomainName);
    setDomainId(nextDomainId);
    setReportDimensions(nextDims);
    setReportMetrics(nextMets);
    setReportSettings(nextSettings);
    setPage(1);
    setChipsExpanded(false);
    setFiltersOpen(true);
    setData(null);
    setProgData(null);
    // Keep the user's current date range — saved filters never restore dates.
    setApplied({
      startDate,
      endDate,
      country: nextCountry,
      domain: nextDomain,
      site: nextSite,
      domainName: nextDomainName,
      domainId: nextDomainId,
      reportDimensions: nextDims,
      reportMetrics: nextMets,
      reportSettings: nextSettings,
    });
    setHasApplied(true);
  }, [startDate, endDate]);

  const load = useCallback(async (silent = false) => {
    if (!canGenerate) return;
    if (!reportClientId) return;
    const query = resolveReportingQuery(applied);
    if (!query) return;
    const { dims, mets } = query;
    const key = `${reportingAppliedKey(applied)}|${reportClientId || ''}`;
    // Coalesce identical in-flight loads (Strict Mode / applied object churn).
    if (loadInFlightRef.current && loadKeyRef.current === key) return;
    if (silent && loadInFlightRef.current) return;

    const loadGen = ++loadGenRef.current;
    // Cancel any in-flight building poll from a prior load.
    buildingPollRef.current += 1;
    const buildingPollId = buildingPollRef.current;
    setBuildingTimedOut(false);
    loadInFlightRef.current = true;
    loadKeyRef.current = key;

    /** Never let a later empty response wipe rows we already have for this query. */
    const applyDetailed = (payload) => {
      if (loadGen !== loadGenRef.current) return;
      setData((prev) => {
        if (!reportPayloadHasRows(payload) && reportPayloadHasRows(prev) && prev?._queryKey === key) {
          if (payload?.status === 'building' || payload?.status === 'partial') {
            return { ...prev, status: payload.status };
          }
          if (payload?.status !== 'ready' && payload?.status !== 'empty') return prev;
        }
        const next = payload ? { ...payload, _queryKey: key, _clientId: reportClientId } : payload;
        dataRef.current = next;
        return next;
      });
    };
    const applyProg = (payload) => {
      if (loadGen !== loadGenRef.current) return;
      setProgData((prev) => {
        if (!reportPayloadHasRows(payload) && reportPayloadHasRows(prev) && prev?._queryKey === key) {
          if (payload?.status === 'building' || payload?.status === 'partial') {
            return { ...prev, status: payload.status };
          }
          if (payload?.status !== 'ready' && payload?.status !== 'empty') return prev;
        }
        const next = payload ? { ...payload, _queryKey: key, _clientId: reportClientId } : payload;
        progDataRef.current = next;
        return next;
      });
    };

    const hasCached = reportPayloadHasRows(dataRef.current) || reportPayloadHasRows(progDataRef.current);
    if (!silent && !hasCached) {
      setLoading(true);
      setSlowLoad(false);
      clearTimeout(slowTimerRef.current);
      slowTimerRef.current = setTimeout(() => setSlowLoad(true), 8000);
    }
    setError(null);
    try {
      const cfg = resolveReportTableConfig(dims, mets);
      const dateFilters = {
        ...normalizeInventorySelections(applied || {}, {}, scopedNormOpts),
        startDate: applied.startDate || todayInit.startDate,
        endDate: applied.endDate || todayInit.endDate,
        reportDimensions: dims,
        reportMetrics: mets,
        country: applied.country,
        reportSettings: applied.reportSettings,
      };
      // Always request the full SQL-capped sample (fair per-day). Sending
      // limit:100 + allRows:false sorted DESC made 30d/3m/6m look like "today only".
      const reportFilters = { ...dateFilters, allRows: true };
      const singleClientId = reportClientId && !String(reportClientId).startsWith('merge:')
        ? reportClientId
        : null;
      let detailed;
      let programmatic;
      if (mergeIds?.length) {
        const detailParts = cfg.mode === 'inventory'
          ? await Promise.all(
            mergeIds.map((id) => reportsAPI.getDetailed(reportFilters, { clientId: id }).catch(() => null))
          )
          : [];
        const progParts = cfg.mode === 'programmatic'
          ? await Promise.all(
            mergeIds.map((id) => reportsAPI.getProgrammatic(reportFilters, { clientId: id }).catch(() => null))
          )
          : [];
        detailed = cfg.mode === 'inventory' ? mergeDashboardResponses(detailParts) : null;
        programmatic = cfg.mode === 'programmatic' ? mergeDashboardResponses(progParts) : null;
      } else {
        const clientCfg = singleClientId ? { clientId: singleClientId } : {};
        [detailed, programmatic] = await Promise.all([
          cfg.mode === 'inventory' ? reportsAPI.getDetailed(reportFilters, clientCfg) : Promise.resolve(null),
          cfg.mode === 'programmatic' ? reportsAPI.getProgrammatic(reportFilters, clientCfg).catch(() => null) : Promise.resolve(null),
        ]);
      }
      if (loadGen !== loadGenRef.current) return;
      applyDetailed(detailed);
      applyProg(programmatic);
      if (reportClientId) {
        reportCacheRef.current.set(String(reportClientId), {
          data: detailed ? { ...detailed, _queryKey: key, _clientId: reportClientId } : null,
          progData: programmatic ? { ...programmatic, _queryKey: key, _clientId: reportClientId } : null,
          key,
        });
      }
      setLastUpdated(nowTimeInTZ());
      setFetchedAt(Date.now());
      // Only poll when we truly have no usable data yet (don't flash building over good rows).
      const needsPoll = (!silent)
        && !reportPayloadHasRows(detailed)
        && !reportPayloadHasRows(programmatic)
        && (
          detailed?.status === 'building'
          || programmatic?.status === 'building'
        );
      if (needsPoll) {
        let tries = 0;
        const poll = async () => {
          if (buildingPollId !== buildingPollRef.current) return;
          tries += 1;
          // ~3 min. Past that the job is stuck or has nothing to return; show the
          // empty-result card instead of an hourglass that never goes away.
          if (tries > BUILDING_POLL_MAX_TRIES) {
            setBuildingTimedOut(true);
            return;
          }
          await new Promise((r) => setTimeout(r, 5000));
          if (buildingPollId !== buildingPollRef.current) return;
          try {
            const [againDetailed, againProg] = await Promise.all([
              cfg.mode === 'inventory' ? reportsAPI.getDetailed(reportFilters, clientCfg) : Promise.resolve(null),
              cfg.mode === 'programmatic'
                ? reportsAPI.getProgrammatic(reportFilters, clientCfg).catch(() => null)
                : Promise.resolve(null),
            ]);
            if (buildingPollId !== buildingPollRef.current) return;
            if (againDetailed) applyDetailed(againDetailed);
            if (againProg) applyProg(againProg);
            const gotRows = reportPayloadHasRows(againDetailed) || reportPayloadHasRows(againProg);
            const stillBuilding = (
              !gotRows
              && (
                againDetailed?.status === 'building'
                || againProg?.status === 'building'
              )
            );
            if (stillBuilding) {
              poll();
              return;
            }
            setLastUpdated(nowTimeInTZ());
            setFetchedAt(Date.now());
            setSlowLoad(false);
          } catch (_) { /* keep last payload */ }
        };
        poll();
      }
    } catch (err) {
      if (loadGen !== loadGenRef.current) return;
      logErrorForDebug(err, 'Reporting load');
      const status = err?.status ?? err?.response?.status ?? null;
      // Auth/permission / timeout / server errors — show error. Do not fake "incompatible".
      if (
        status === 401
        || status === 403
        || status === 503
        || status === 502
        || status === 504
        || status === 500
        || err?.isTimeout
        || err?.code === 'ECONNABORTED'
        || err?.code === 'ERR_NETWORK'
      ) {
        setError(getUserFacingMessage(err, 'Could not load the report. Please try again or narrow your filters.'));
        if (!reportPayloadHasRows(dataRef.current)) {
          setData(null);
          setProgData(null);
        }
      } else {
        setError(null);
        setData({
          rows: [],
          summary: { totalRevenue: 0, totalDomains: 0, offeredRecords: 0 },
          trend: [],
          reportWarning: 'incompatible',
          reportWarningSkipped: [
            ...dims.map((id) => dimensionLabel(id)),
            ...mets.map((id) => metricLabel(id)),
          ],
          _queryKey: key,
        });
        setProgData(null);
        setLastUpdated(nowTimeInTZ());
        setFetchedAt(Date.now());
      }
    } finally {
      if (loadGen === loadGenRef.current) {
        loadInFlightRef.current = false;
        if (!silent) {
          setLoading(false);
          setSlowLoad(false);
          clearTimeout(slowTimerRef.current);
        }
      }
    }
  }, [applied, todayInit.startDate, todayInit.endDate, canGenerate, scopedNormOpts, reportClientId, mergeIds]);

  const appliedQueryKey = useMemo(() => reportingAppliedKey(applied), [applied]);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  useEffect(() => {
    progDataRef.current = progData;
  }, [progData]);

  useEffect(() => {
    if (!canGenerate) return;
    // Don't load anything until the user has applied a filter at least once.
    if (!hasApplied) return;
    if (skipInitialLoadRef.current) {
      skipInitialLoadRef.current = false;
      // Paint cache, then refresh so F5 never sticks on stale report rows.
      loadRef.current(true);
      return;
    }
    reportCacheRef.current.clear();
    loadRef.current();
    setPage(1);
  }, [canGenerate, appliedQueryKey, hasApplied]);

  // Soft network switch from sidebar — keep scroll, swap cache, refresh silently.
  const reportClientIdRef = useRef(reportClientId);
  useEffect(() => {
    if (!hasApplied) return;
    const prev = reportClientIdRef.current;
    reportClientIdRef.current = reportClientId;
    if (!reportClientId || String(prev) === String(reportClientId)) return;
    setCatalog([]);
    const cached = reportCacheRef.current.get(String(reportClientId));
    if (cached && cached.key?.startsWith(reportingAppliedKey(applied))) {
      setData(cached.data);
      dataRef.current = cached.data;
      setProgData(cached.progData);
      progDataRef.current = cached.progData;
      setLoading(false);
    } else {
      setData(null);
      dataRef.current = null;
      setProgData(null);
      progDataRef.current = null;
    }
    loadRef.current(true);
  }, [reportClientId, hasApplied, applied]);
  useEffect(() => {
    if (!canGenerate) return;
    if (!data || !fetchedAt) return;
    // Persist overview + applied filters so refresh keeps card values.
    dispatch(saveReportPage({
      pageKey: 'reporting',
      payload: {
        userId: user?.id,
        clientId: reportClientId || user?.clientId || null,
        applied, data, progData, catalog, fetchedAt, lastUpdated,
        preset, startDate, endDate, country, domain, site, domainName, domainId,
        search, page, filtersOpen, breakdownOpen, chipsExpanded,
        reportDimensions, reportMetrics, reportSettings,
      },
    }));
  }, [
    dispatch, user?.id, reportClientId, applied, data, progData, catalog, fetchedAt, lastUpdated,
    preset, startDate, endDate, country, domain, site, domainName, domainId,
    search, page, filtersOpen, breakdownOpen, chipsExpanded,
    reportDimensions, reportMetrics, reportSettings,
  ]);

  // Capture the full row set as the option catalogue whenever an UNFILTERED
  // report loads (no inventory filter applied), so the dropdowns always have the
  // complete list to cascade from.
  useEffect(() => {
    if (!data?.rows) return;
    if (applied.domain?.length || applied.site?.length || applied.domainName?.length || applied.domainId?.length) return;
    setCatalog(data.rows);
  }, [data, applied.domain, applied.site, applied.domainName, applied.domainId]);

  const applyCatalogResponse = useCallback((res) => {
    if (res?.rows?.length) setCatalog(res.rows);
    if (Array.isArray(res?.domainRoots) || Array.isArray(res?.siteHosts) || res?.sitesByDomain || res?.adUnitsByHost || res?.appPackages) {
      setCatalogLists({
        domainRoots: res.domainRoots || [],
        siteHosts: res.siteHosts || [],
        sitesByDomain: res.sitesByDomain || {},
        adUnitsByHost: res.adUnitsByHost || {},
        appIds: (res.appPackages || []).filter((id) => id && id !== '—'),
      });
    }
    if (typeof res?.noDomainsAssigned === 'boolean') setNoDomainsAssigned(res.noDomainsAssigned);
  }, []);

  const loadCatalog = useCallback(async (force = false) => {
    // Domain users: granted domain/site/app lists are already on the session user.
    if (filterVisibility.isScopedUser) {
      setCatalogLoading(false);
      return;
    }
    if (!force && catalog.length) return;
    setCatalogLoading(true);
    try {
      const res = await reportsAPI.getFilterCatalog(reportClientId || user?.clientId);
      applyCatalogResponse(res);
    } catch (err) {
      logErrorForDebug(err, 'Reporting filter catalog');
      setError(getUserFacingMessage(err, 'Could not load filter options. Please refresh the page.'));
    } finally { setCatalogLoading(false); }
  }, [catalog.length, applyCatalogResponse, filterVisibility.isScopedUser, user?.clientId, reportClientId]);

  useEffect(() => {
    if (!canGenerate) return;
    if (filterVisibility.isScopedUser) {
      setCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCatalogLoading(true);
    reportsAPI.getFilterCatalog(reportClientId || user?.clientId)
      .then((res) => { if (!cancelled) applyCatalogResponse(res); })
      .catch((err) => {
        if (!cancelled) {
          logErrorForDebug(err, 'Reporting filter catalog (initial)');
          setError(getUserFacingMessage(err, 'Could not load filter options. Please refresh the page.'));
        }
      })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [applyCatalogResponse, canGenerate, filterVisibility.isScopedUser, user?.clientId, reportClientId]);
  // Selections keyed by filter field; drives bidirectional cascading so picking
  // any filter instantly narrows the other three (no Generate needed).
  const selections = useMemo(
    () => ({
      domain: isAllSelection(domain) ? [] : (domain || []).filter((v) => v !== ALL_SENTINEL),
      site: isAllSelection(site) ? [] : (site || []).filter((v) => v !== ALL_SENTINEL),
      adUnit: isAllSelection(domainName) ? [] : (domainName || []).filter((v) => v !== ALL_SENTINEL),
      app: isAllSelection(domainId) ? [] : (domainId || []).filter((v) => v !== ALL_SENTINEL),
    }),
    [domain, site, domainName, domainId]
  );
  const { domainOptions: domainRootOptions, siteOptions, adUnitOptions, appOptions } = useMemo(
    () => buildFilterDropdownOptions({
      catalog,
      selections,
      domainRoots: catalogLists.domainRoots,
      siteHosts: catalogLists.siteHosts,
      sitesByDomain: catalogLists.sitesByDomain,
      adUnitsByHost: catalogLists.adUnitsByHost,
      selectedDomains: isAllSelection(domain) ? [] : domain,
      inventoryScope,
      independentAssignment: true,
      allowedDomains: inventoryScope?.allowedDomains ?? null,
      appIds: catalogLists.appIds,
    }),
    [catalog, selections, catalogLists, domain, inventoryScope, filterVisibility.isScopedUser]
  );
  const catalogBusy = !filterVisibility.isScopedUser && catalogLoading;

  const showNoDomainsNote = !isAdmin(user) && (!inventoryAssigned || noDomainsAssigned);

  const handleDomainChange = useCallback((nextDomain) => {
    setDomain(nextDomain);
  }, []);

  const handleSiteChange = useCallback((nextSite) => {
    setSite(nextSite);
  }, []);

  const handleAdUnitChange = useCallback((nextAdUnit) => {
    setDomainName(nextAdUnit);
  }, []);

  const handleAppChange = useCallback((nextApp) => {
    setDomainId(nextApp);
  }, []);

  // Columns the table auto-shows for the current inventory (breakdown) filters.
  // App ID pulls in BOTH App ID + App names (matches real GAM's default app
  // report), so every app resolves + is counted and revenue totals stay correct.
  const autoInventoryDims = useMemo(() => {
    const dims = [];
    if (country?.length) dims.push('country_name');
    if (domain?.length) dims.push('domain');
    if (site?.length) dims.push('site_name');
    if (domainName?.length) dims.push('ad_unit_name');
    if (domainId?.length) {
      dims.push('mobile_app_resolved_id');
      dims.push('mobile_app_name');
    }
    if (dims.length) dims.unshift('date');
    return dims;
  }, [country, domain, site, domainName, domainId]);

  // Mirror those auto columns into the Report Builder dimension picker so they
  // show as already selected — the user never has to re-check them (and can't
  // accidentally break the report by selecting only part of the set). Manual
  // dimension choices are preserved, except sticky default Domain/Site are
  // dropped for App-only filters so App×Country stays on the app_id slice.
  const autoDimsRef = useRef([]);
  useEffect(() => {
    setReportDimensions((prev) => {
      const prevAuto = autoDimsRef.current;
      const appOnly = Boolean(domainId?.length)
        && !(domain?.length)
        && !(site?.length)
        && !(domainName?.length);
      let manual = (prev || []).filter(
        (d) => !prevAuto.includes(d) && !autoInventoryDims.includes(d)
      );
      if (appOnly) {
        manual = manual.filter((d) => d !== 'domain' && d !== 'site_name' && d !== 'url_name');
      }
      const next = [...autoInventoryDims, ...manual];
      // Keep Dashboard-like date×domain×site defaults when nothing is selected.
      if (!next.length && !appOnly) return [...DEFAULT_REPORT_DIMENSIONS];
      return next;
    });
    autoDimsRef.current = autoInventoryDims;
  }, [autoInventoryDims, domainId, domain, site, domainName]);

  // Load the (lightweight) country list once for the filter dropdown.
  useEffect(() => {
    if (!canGenerate) return;
    reportsAPI.getCountries()
      .then(setCountryOptions)
      .catch(() => setCountryOptions([]))
      .finally(() => setCountryLoading(false));
  }, [canGenerate]);

  // Realtime auto-refresh for overview cards + any applied report
  useEffect(() => {
    if (!canGenerate) return undefined;
    if (!hasApplied) return undefined;
    if (!resolveReportingQuery(applied)) return undefined;
    pollRef.current = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [load, appliedQueryKey, canGenerate, hasApplied]);

  // Default metrics always allow Apply; scoped users still need inventory when they clear defaults.
  const inventoryDraft = useMemo(
    () => ({ domain, site, domainName, domainId }),
    [domain, site, domainName, domainId]
  );
  const canRunReport = Boolean(resolveReportingQuery({
    reportDimensions, reportMetrics, domain, site, domainName, domainId, country,
  })) && (!filterVisibility.isScopedUser || draftHasInventorySelection(inventoryDraft)
    || reportDimensions.length || reportMetrics.length);
  const customDatesIncomplete = isCustomRangeIncomplete(preset, startDate, endDate);
  useEffect(() => {
    if (!canRunReport) {
      setFiltersOpen(true);
    }
  }, [canRunReport]);

  const applyPreset = (p) => {
    if (dateFilterLocked) return;
    if (dateRestriction && !isPresetAllowedForRestriction(p, dateRestriction)) return;
    setPreset(p);
    if (p === 'custom') {
      setStartDate('');
      setEndDate('');
      setBreakdownOpen(true);
      loadCatalog(true);
      return;
    }
    // Predefined range: immediately update applied → triggers load() automatically.
    const r = clampPresetRange(p, dateRestriction);
    setStartDate(r.startDate);
    setEndDate(r.endDate);
    setPage(1);
    setChipsExpanded(false);
    if (resolveReportingQuery({ ...applied, startDate: r.startDate, endDate: r.endDate })) {
      setData(null);
      setProgData(null);
    }
    // Keep inventory + dims/metrics — only update dates (do not rebuild full scoped inventory).
    setApplied((prev) => ({
      ...prev,
      startDate: r.startDate,
      endDate: r.endDate,
    }));
    if (scopedAutoLoad || hasApplied) {
      setHasApplied(true);
    }
  };

  const applyFilter = () => {
    if (!canRunReport) return;
    let sd = startDate;
    let ed = endDate;
    if (preset === 'custom') {
      const today = todayInTZ();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(sd || ''))) sd = /^\d{4}-\d{2}-\d{2}$/.test(String(ed || '')) ? ed : today;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ed || ''))) ed = /^\d{4}-\d{2}-\d{2}$/.test(String(sd || '')) ? sd : today;
      if (sd > ed) { const t = sd; sd = ed; ed = t; }
    }
    const dates = clampDateRange(sd, ed, dateRestriction);
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
    setPage(1);
    setData(null);
    setProgData(null);
    setHasApplied(true);
    setActiveQuickView(null);
    setSegmentFilter(null);
    // Keep Select-All sentinel in UI; API path normalizes to [].
    setApplied({
      ...dates,
      country,
      domainName,
      domainId,
      domain,
      site,
      reportDimensions,
      reportMetrics,
      reportSettings,
    });
    persistRecentFilter();
    saveLastPageFilters(LAST_FILTER_PAGES.reporting, {
      preset,
      startDate: dates.startDate,
      endDate: dates.endDate,
      country,
      domain,
      site,
      domainName,
      domainId,
      reportDimensions,
      reportMetrics,
      reportSettings,
    }, user?.id);
    setFiltersOpen(false);
    setChipsExpanded(false);
    loadCatalog(true);
    const qs = encodeReportShare({
      preset,
      startDate: dates.startDate,
      endDate: dates.endDate,
      domain,
      site,
      domainName,
      domainId,
      country,
      reportDimensions,
      reportMetrics,
    });
    setSearchParams(qs ? new URLSearchParams(qs) : {}, { replace: true });
    const rangeLabel = dates.startDate === dates.endDate
      ? dates.startDate
      : `${dates.startDate} → ${dates.endDate}`;
    showToast({ message: `Loaded ${rangeLabel}`, replaceKey: 'filter-loaded' });
  };

  const applyQuickView = (view) => {
    if (!canFilter || !view) return;
    if (view.preset === 'today' && !view.dims && !view.mets) {
      setActiveQuickView(view.id);
      applyPreset('today');
      return;
    }
    if (dateRestriction && view.preset && !isPresetAllowedForRestriction(view.preset, dateRestriction)) {
      return;
    }
    const dates = view.preset && view.preset !== 'custom'
      ? clampPresetRange(view.preset, dateRestriction)
      : clampDateRange(startDate, endDate, dateRestriction);
    const nextDims = view.dims?.length ? view.dims : reportDimensions;
    const nextMets = view.mets?.length ? view.mets : reportMetrics;
    setActiveQuickView(view.id);
    setSegmentFilter(null);
    if (view.preset) setPreset(view.preset);
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
    setReportDimensions(nextDims);
    setReportMetrics(nextMets);
    setPage(1);
    setData(null);
    setProgData(null);
    setHasApplied(true);
    setChipsExpanded(false);
    setApplied({
      startDate: dates.startDate,
      endDate: dates.endDate,
      country,
      domain,
      site,
      domainName,
      domainId,
      reportDimensions: nextDims,
      reportMetrics: nextMets,
      reportSettings,
    });
  };

  const handleChartSegmentClick = useCallback((segment) => {
    if (!segment?.dimensionId || !segment?.value || segment.value === 'Others') return;
    setSegmentFilter((prev) => (
      prev?.dimensionId === segment.dimensionId && prev?.value === segment.value
        ? null
        : { dimensionId: segment.dimensionId, value: segment.value }
    ));
    setPage(1);
  }, []);

  const appliedChips = useMemo(
    () => buildAppliedFilterChips(applied, {
      countryOptions,
      domainOptions: domainRootOptions,
      siteOptions,
      adUnitOptions,
      appOptions,
    }),
    [applied, countryOptions, domainRootOptions, siteOptions, adUnitOptions, appOptions]
  );
  const allAppliedChips = useMemo(() => {
    // Only show dim/metric chips for non-default selections so reset clears all chips visually.
    const nonDefaultDims = (applied.reportDimensions || []).filter(d => !DEFAULT_REPORT_DIMENSIONS.includes(d));
    const nonDefaultMets = (applied.reportMetrics || []).filter(m => !DEFAULT_REPORT_METRICS.includes(m));
    return [
      ...appliedChips,
      ...dimensionsToChips(nonDefaultDims),
      ...metricsToChips(nonDefaultMets),
      ...reportSettingsToChips(applied.reportSettings || DEFAULT_REPORT_SETTINGS),
    ];
  }, [appliedChips, applied.reportDimensions, applied.reportMetrics, applied.reportSettings]);

  const toggleChipsExpanded = () => setChipsExpanded((v) => !v);

  const toggleFiltersPanel = () => {
    setFiltersOpen((open) => {
      const next = !open;
      if (!next) {
        setChipsExpanded(false);
        setBreakdownOpen(false);
      }
      return next;
    });
  };

  const scrollToReportBuilder = () => {
    setFiltersOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        filterPanelRef.current
          ?.querySelector('.gam-report-builder-section')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const handleAddFilter = () => {
    scrollToReportBuilder();
  };

  const reportingQuery = useMemo(() => resolveReportingQuery(applied), [applied]);
  const hasUserReport = Boolean(reportingQuery);
  const effectiveAppliedDims = reportingQuery?.tableDims ?? reportingQuery?.dims ?? [];
  const effectiveAppliedMets = reportingQuery?.mets ?? [];
  const reportReady = hasApplied && hasUserReport && hasActiveReport(effectiveAppliedDims, effectiveAppliedMets);

  const serverSummary = data?.summary || progData?.summary || {};
  const summaryLoading = hasUserReport && (loading || (canGenerate && !data && !progData));
  const vis = {
    ...clientVis,
    ...(data?.visibility || progData?.visibility || {}),
  };
  const canDownload = vis.download !== false;
  const canFilter = vis.filters !== false;
  const canReportBuilder = vis.reportBuilder !== false;

  // When GAM returns a partial-query warning, keep only dims/metrics that actually ran.
  // Prefer catalog ids (reportWarningUsedIds / reportWarningUsedMetricIds).
  const effectiveDims = useMemo(() => {
    if (!data?.reportWarning) return effectiveAppliedDims;

    const usedIds = new Set(
      (data.reportWarningUsedIds || []).map((s) => String(s).toLowerCase())
    );
    if (usedIds.size) {
      const matched = effectiveAppliedDims.filter((id) => usedIds.has(String(id).toLowerCase()));
      // Prefer matched columns; if ids don't align (legacy payloads), keep applied dims
      // so compatible rows still render.
      if (matched.length) return matched;
      if (Array.isArray(data?.rows) && data.rows.length) return effectiveAppliedDims;
    }

    const LABEL_ALIASES = {
      'app id': ['app id', 'package name'],
      'app names': ['app names', 'app name'],
    };
    const usedLabels = new Set(
      (data.reportWarningUsed || []).map((s) => String(s).toLowerCase())
    );
    if (usedLabels.size) {
      const matched = effectiveAppliedDims.filter((id) => {
        const label = dimensionLabel(id).toLowerCase();
        if (usedLabels.has(label)) return true;
        const aliases = LABEL_ALIASES[label] || [];
        return aliases.some((a) => usedLabels.has(a));
      });
      if (matched.length) return matched;
      if (Array.isArray(data?.rows) && data.rows.length) return effectiveAppliedDims;
    }
    return effectiveAppliedDims;
  }, [effectiveAppliedDims, data]);

  const effectiveMets = useMemo(() => {
    const substitutions = Array.isArray(data?.reportWarningSubstitutions)
      ? data.reportWarningSubstitutions
      : [];
    const applySubs = (ids) => (ids || []).map((id) => {
      const hit = substitutions.find((s) => String(s.from).toLowerCase() === String(id).toLowerCase());
      return hit?.to || id;
    });

    if (!data?.reportWarning) {
      return substitutions.length ? applySubs(effectiveAppliedMets) : effectiveAppliedMets;
    }
    const usedMetIds = new Set(
      (data.reportWarningUsedMetricIds || []).map((s) => String(s).toLowerCase())
    );
    if (usedMetIds.size) {
      const swapped = applySubs(effectiveAppliedMets);
      const matched = swapped.filter((id) => usedMetIds.has(String(id).toLowerCase()));
      // Auto-substituted metrics (e.g. Programmatic eligible ad requests) may not
      // be in the user's selection — append any used ids we still need to show.
      for (const id of usedMetIds) {
        if (!matched.some((m) => String(m).toLowerCase() === id)) {
          const fromApplied = swapped.find((m) => String(m).toLowerCase() === id);
          matched.push(fromApplied || id);
        }
      }
      if (matched.length) return matched;
      if (Array.isArray(data?.rows) && data.rows.length) return swapped;
    }
    // Partial response without metric ids: drop metrics listed as skipped by label.
    const skipped = new Set(
      (data.reportWarningSkipped || []).map((s) => String(s).toLowerCase())
    );
    if (!skipped.size) return applySubs(effectiveAppliedMets);
    const kept = applySubs(effectiveAppliedMets).filter((id) => {
      const label = String(metricLabel(id)).toLowerCase();
      if (skipped.has(label)) return false;
      // "Total ad requests → Programmatic …" chips should not hide the substitute.
      for (const s of skipped) {
        if (s.startsWith(`${label} →`)) return false;
      }
      return true;
    });
    return kept.length ? kept : applySubs(effectiveAppliedMets);
  }, [effectiveAppliedMets, data]);

  const tableConfig = useMemo(
    () => resolveReportTableConfig(effectiveDims, effectiveMets),
    [effectiveDims, effectiveMets]
  );
  const reportColumns = useMemo(
    () => buildReportColumns(tableConfig.dimensions, tableConfig.metrics, vis),
    [tableConfig, vis]
  );

  const tableRows = useMemo(() => {
    if (tableConfig.mode === 'none') return [];
    // When there's a warning, prefer partial result rows; otherwise use mode source.
    const raw = data?.reportWarning && Array.isArray(data?.rows) && data.rows.length
      ? data.rows
      : tableConfig.mode === 'programmatic'
        ? (progData?.rows || [])
        : (data?.rows || []);
    const enriched = enrichReportRows(raw, tableConfig.dimensions, tableConfig.metrics);
    // Roll up to visible columns so site × country isn't split by hidden domain_id variants.
    const rolled = aggregateRowsByColumns(enriched, reportColumns);
    if (tableConfig.mode === 'inventory') return rolled;
    return sortRowsByCompleteness(rolled, reportColumns);
  }, [tableConfig, progData, data, reportColumns]);

  const displayRows = useMemo(() => {
    if (!segmentFilter?.dimensionId || !segmentFilter?.value) return tableRows;
    return tableRows.filter((row) => (
      rowMatchesChartDimension(row, segmentFilter.dimensionId, segmentFilter.value)
    ));
  }, [tableRows, segmentFilter]);

  useEffect(() => {
    setSegmentFilter(null);
  }, [applied]);

  const totalRecordCount = tableRows.length;

  // Cards come from the rows the table is showing; the server summary only fills in before any rows exist.
  const summary = useMemo(
    () => (displayRows.length
      ? { ...serverSummary, ...summarizeReportingRows(displayRows, reportColumns) }
      : serverSummary),
    [displayRows, reportColumns, serverSummary]
  );

  // Only treat as "no data" when the report is empty. If GAM returned a partial
  // compatible subset, still show those rows and warn about what was skipped.
  const skippedChips = useMemo(() => {
    if (data?.reportWarningSkipped?.length) return data.reportWarningSkipped;
    return [];
  }, [data?.reportWarningSkipped]);

  const unavailableChips = useMemo(() => {
    if (skippedChips.length) return skippedChips;
    if (!loading && reportReady && totalRecordCount === 0 && data) {
      return [
        ...effectiveAppliedDims.map((id) => dimensionLabel(id)),
        ...effectiveAppliedMets.map((id) => metricLabel(id)),
      ];
    }
    return [];
  }, [
    skippedChips,
    data,
    loading,
    reportReady,
    totalRecordCount,
    effectiveAppliedDims,
    effectiveAppliedMets,
  ]);

  const stillBuilding = Boolean(
    (data?.status === 'building' || progData?.status === 'building')
    && !buildingTimedOut
    && totalRecordCount === 0
  );

  const showNoReportCard = Boolean(
    reportReady
    && !loading
    && totalRecordCount === 0
    && (data || progData)
    && !stillBuilding
  );

  /**
   * The report ran and simply had nothing to return — different message from
   * "these dimensions can't be combined".
   */
  const isEmptyResult = Boolean(
    showNoReportCard
    && (buildingTimedOut || data?.status === 'empty' || progData?.status === 'empty')
    && !skippedChips.length
  );

  const clearIncompatibleReporting = () => {
    const skip = new Set(unavailableChips.map((s) => String(s).toLowerCase()));
    const subFrom = new Set(
      (data?.reportWarningSubstitutions || []).map((s) => String(s.from).toLowerCase())
    );
    // Chips like "Total ad requests → Programmatic eligible ad requests"
    for (const chip of unavailableChips) {
      const left = String(chip).split('→')[0].trim().toLowerCase();
      if (left) skip.add(left);
    }
    const nextDims = reportDimensions.filter((id) => (
      !skip.has(String(dimensionLabel(id)).toLowerCase()) && !skip.has(String(id).toLowerCase())
    ));
    const nextMets = reportMetrics.filter((id) => {
      const key = String(id).toLowerCase();
      if (subFrom.has(key)) return false;
      return !skip.has(String(metricLabel(id)).toLowerCase()) && !skip.has(key);
    });
    const dropDomain = skip.has('domain name');
    const dropSite = skip.has('site');
    const dropAd = skip.has('ad unit');
    const dropApp = skip.has('app id');
    if (dropDomain) setDomain([]);
    if (dropSite) setSite([]);
    if (dropAd) setDomainName([]);
    if (dropApp) setDomainId([]);
    setReportDimensions(nextDims);
    if (nextMets.length) setReportMetrics(nextMets);
    setHasApplied(true);
    setApplied((prev) => ({
      ...prev,
      reportDimensions: nextDims,
      reportMetrics: nextMets.length ? nextMets : prev.reportMetrics,
      domain: dropDomain ? [] : prev.domain,
      site: dropSite ? [] : prev.site,
      domainName: dropAd ? [] : prev.domainName,
      domainId: dropApp ? [] : prev.domainId,
    }));
    showToast({ message: 'Removed incompatible fields' });
  };

  const showPartialCompatWarning = Boolean(
    reportReady
    && !loading
    && totalRecordCount > 0
    && skippedChips.length > 0
  );

  // Keep cards/table visible during refresh if we already have rows (don't gate on !loading).
  const hasReportData = totalRecordCount > 0;
  const showSummaryCards = canGenerate && hasApplied && (hasReportData || loading);
  // Only show the hourglass when we are waiting AND have nothing useful on screen yet.
  const showBuildingBanner = Boolean(stillBuilding && !hasReportData);

  const handleRemoveChip = (chip) => {
    if (chip.field === 'date') {
      const r = defaultReportRangeForUser(user);
      setPreset('today');
      setStartDate(r.startDate);
      setEndDate(r.endDate);
      setData(null);
      setProgData(null);
      setApplied(prev => ({ ...prev, startDate: r.startDate, endDate: r.endDate }));
      return;
    }
    if (chip.field === 'dimension') {
      const nextDims = (applied.reportDimensions || []).filter((d) => d !== chip.value);
      setReportDimensions(nextDims);
      setApplied((prev) => ({ ...prev, reportDimensions: nextDims }));
      return;
    }
    if (chip.field === 'metric') {
      const nextMets = (applied.reportMetrics || []).filter((m) => m !== chip.value);
      setReportMetrics(nextMets);
      setApplied((prev) => ({ ...prev, reportMetrics: nextMets }));
      return;
    }
    if (chip.field === 'reportSetting' && chip.settingKey) {
      const nextSettings = {
        ...(applied.reportSettings || DEFAULT_REPORT_SETTINGS),
        [chip.settingKey]: DEFAULT_REPORT_SETTINGS[chip.settingKey],
      };
      setReportSettings(nextSettings);
      setApplied((prev) => ({ ...prev, reportSettings: nextSettings }));
      return;
    }
    const draft = { startDate, endDate, country, domain, site, domainName, domainId };
    const { nextApplied, nextDraft } = removeFilterChip(applied, draft, chip, {
      countryOptions,
      domainOptions: domainRootOptions,
      siteOptions,
      adUnitOptions,
      appOptions,
    });
    setCountry(nextDraft.country || []);
    setDomain(nextDraft.domain || []);
    setSite(nextDraft.site || []);
    setDomainName(nextDraft.domainName || []);
    setDomainId(nextDraft.domainId || []);
    setApplied(nextApplied);
  };

  const reset = () => {
    undoSnapRef.current = {
      preset, startDate, endDate, country, domain, site, domainName, domainId,
      reportDimensions, reportMetrics, reportSettings, applied, hasApplied,
    };
    const r = defaultReportRangeForUser(user);
    setPreset('today');
    setStartDate(r.startDate);
    setEndDate(r.endDate);
    setCountry([]);
    setDomain([]);
    setSite([]);
    setDomainName([]);
    setDomainId([]);
    setReportDimensions([...DEFAULT_REPORT_DIMENSIONS]);
    setReportMetrics([...DEFAULT_REPORT_METRICS]);
    setReportSettings(DEFAULT_REPORT_SETTINGS);
    setFiltersOpen(!filterVisibility.isScopedUser);
    setBreakdownOpen(true);
    setChipsExpanded(false);
    setSearch('');
    setPage(1);
    setData(null);
    setProgData(null);
    setFetchedAt(null);
    setHasApplied(false);
    setLoading(false);
    setError(null);
    clearRecentFilters(user?.id);
    setRecentFilters([]);
    setActiveQuickView(null);
    setSegmentFilter(null);
    clearLastPageFilters(LAST_FILTER_PAGES.reporting, user?.id);
    dispatch(saveReportPage({ pageKey: 'reporting', payload: null }));
    setApplied({
      ...r,
      ...EMPTY_INVENTORY_FILTERS,
      country: [],
      reportDimensions: [...DEFAULT_REPORT_DIMENSIONS],
      reportMetrics: [...DEFAULT_REPORT_METRICS],
      reportSettings: DEFAULT_REPORT_SETTINGS,
    });
    setSearchParams({}, { replace: true });
    showToast({
      message: 'Filters cleared',
      replaceKey: 'filter-status',
      actionLabel: 'Undo',
      onAction: () => {
        const snap = undoSnapRef.current;
        if (!snap) return;
        setPreset(snap.preset);
        setStartDate(snap.startDate);
        setEndDate(snap.endDate);
        setCountry(snap.country || []);
        setDomain(snap.domain || []);
        setSite(snap.site || []);
        setDomainName(snap.domainName || []);
        setDomainId(snap.domainId || []);
        setReportDimensions(snap.reportDimensions || []);
        setReportMetrics(snap.reportMetrics || [...DEFAULT_REPORT_METRICS]);
        setReportSettings(snap.reportSettings || DEFAULT_REPORT_SETTINGS);
        setApplied(snap.applied);
        setHasApplied(snap.hasApplied);
      },
    });
  };

  const handleCopyLink = async () => {
    await copyReportLink({
      preset,
      startDate: applied?.startDate || startDate,
      endDate: applied?.endDate || endDate,
      domain: applied?.domain || domain,
      site: applied?.site || site,
      domainName: applied?.domainName || domainName,
      domainId: applied?.domainId || domainId,
      country: applied?.country || country,
      reportDimensions: applied?.reportDimensions || reportDimensions,
      reportMetrics: applied?.reportMetrics || reportMetrics,
    });
    showToast({ message: 'Link copied — opens this exact report' });
  };

  useReportHotkeys({
    enabled: canGenerate && canFilter,
    onApply: () => {
      if (!canRunReport) return;
      applyFilter();
    },
    onReset: reset,
  });

  /**
   * Every row of the applied report for CSV / Excel. The table on screen is a capped sample of big reports
   * (response.pagination.truncated), so the export asks the server again with exportAll, then runs the rows
   * through the same enrich / roll-up steps as the table. Returns null when the rows on screen are all there is
   * (merged networks and programmatic reports keep using them).
   */
  const fetchAllRowsForExport = useCallback(async () => {
    const query = hasApplied ? resolveReportingQuery(applied) : null;
    if (!query || tableConfig.mode !== 'inventory' || mergeIds?.length) return null;
    const singleClientId = reportClientId && !String(reportClientId).startsWith('merge:') ? reportClientId : null;
    const payload = await reportsAPI.getDetailed({
      ...normalizeInventorySelections(applied || {}, {}, scopedNormOpts),
      startDate: applied.startDate || todayInit.startDate,
      endDate: applied.endDate || todayInit.endDate,
      reportDimensions: query.dims,
      reportMetrics: query.mets,
      country: applied.country,
      reportSettings: applied.reportSettings,
      allRows: true,
      exportAll: true,
    }, { ...(singleClientId ? { clientId: singleClientId } : {}), timeout: 10 * 60 * 1000 });
    if (!payload || payload.status === 'building' || !Array.isArray(payload.rows)) return null;
    const enriched = enrichReportRows(payload.rows, tableConfig.dimensions, tableConfig.metrics);
    let rows = aggregateRowsByColumns(enriched, reportColumns);
    if (segmentFilter?.dimensionId && segmentFilter?.value) {
      rows = rows.filter((row) => rowMatchesChartDimension(row, segmentFilter.dimensionId, segmentFilter.value));
    }
    return {
      rows,
      truncated: Boolean(payload.pagination?.truncated),
      totalRows: payload.pagination?.offeredRecords ?? payload.pagination?.totalRows ?? rows.length,
    };
  }, [hasApplied, applied, tableConfig, mergeIds, reportClientId, scopedNormOpts, todayInit, reportColumns, segmentFilter]);

  /** Rows to write: the full report when the screen shows a capped sample, else what is on screen. */
  const exportBody = async () => {
    let rows = displayRows;
    let note = '';
    if (data?.pagination?.truncated) {
      showToast({ message: 'Preparing the full export…' });
      try {
        const full = await fetchAllRowsForExport();
        if (full && full.rows.length >= displayRows.length) {
          rows = full.rows;
          if (full.truncated) {
            note = ` — export limit reached, narrow the filters for the rest (${Number(full.totalRows).toLocaleString()} rows exist)`;
          }
        }
      } catch (err) {
        note = ' — could not fetch the full report, exported the rows on screen';
      }
    }
    return { body: rows.map((r) => reportColumns.map((col) => exportCellValue(col, r))), note };
  };

  const downloadCSV = async () => {
    const headers = reportColumns.map((c) => c.label);
    const { body, note } = await exportBody();
    downloadCsv(`report_${applied.startDate}_${applied.endDate}`, headers, body);
    showToast({ message: `Downloaded ${body.length.toLocaleString()} rows (CSV)${note}` });
  };

  const downloadXls = async () => {
    const headers = reportColumns.map((c) => c.label);
    const { body, note } = await exportBody();
    downloadExcel(`report_${applied.startDate}_${applied.endDate}`, headers, body, 'Report');
    showToast({ message: `Downloaded ${body.length.toLocaleString()} rows (Excel)${note}` });
  };

  if (!canGenerate) {
    return (
      <div className="reporting-page">
        <PageHeader
          title="Reporting"
          subtitle="Historical report builder — select dimensions and metrics, then apply filter"
        />
        <AccessRestricted title={NO_VIEW_REPORTS_TITLE} message={NO_VIEW_REPORTS_MSG} />
      </div>
    );
  }

  const reportFilterSummary = (() => {
    if (!hasApplied) return 'Pick dates and fields, then Apply Filter';
    const parts = [];
    if (applied.startDate && applied.endDate) {
      parts.push(applied.startDate === applied.endDate
        ? applied.startDate
        : `${applied.startDate} → ${applied.endDate}`);
    }
    const dimCount = (applied.reportDimensions || []).length;
    const metCount = (applied.reportMetrics || []).length;
    if (dimCount) parts.push(`${dimCount} dimension${dimCount === 1 ? '' : 's'}`);
    if (metCount) parts.push(`${metCount} metric${metCount === 1 ? '' : 's'}`);
    const inv = [];
    if (applied.domain?.length && !isAllSelection(applied.domain)) inv.push(`${applied.domain.length} domains`);
    if (applied.site?.length && !isAllSelection(applied.site)) inv.push(`${applied.site.length} sites`);
    if (applied.country?.length) inv.push(`${applied.country.length} countries`);
    if (inv.length) parts.push(inv.join(', '));
    if (segmentFilter?.value) parts.push(`chart: ${segmentFilter.value}`);
    return parts.join(' · ') || 'Report applied';
  })();

  return (
    <div className="reporting-page">
      <PageHeader
        title="Reporting"
        subtitle="Build historical GAM reports — dimensions, metrics, and inventory filters"
        summary={reportFilterSummary}
      >
        {canFilter && (
          <>
            <button type="button" className="btn-reset btn-copy-link" onClick={handleCopyLink}>
              Copy link
            </button>
            <SavePresetButton
              page={PRESET_PAGES.reporting}
              userId={user?.id}
              getSnapshot={getPresetSnapshot}
              disabled={!canFilter}
            />
          </>
        )}
      </PageHeader>
      <DataFreshness
        coverage={data?.coverage || progData?.coverage}
        networkInfo={networkInfo}
        status={data?.status || progData?.status}
        className="report-freshness"
      />
      {dateRestriction && (
        <p className="form-note page-restriction-note">
          {dateFilterLocked
            ? `Data locked to: ${formatDateRestrictionLabel(dateRestriction)}`
            : `Allowed filter window: ${formatDateRestrictionLabel(dateRestriction)}`}
        </p>
      )}
      <TimezoneNote info={data?.timezone || progData?.timezone} />

      <div className="quick-views-row" role="group" aria-label="Quick views">
        <span className="quick-views-label">Quick views</span>
        <div className="preset-pills quick-views-pills">
          {QUICK_VIEWS.map((view) => (
            <button
              key={view.id}
              type="button"
              className={`preset-pill${activeQuickView === view.id ? ' active' : ''}`}
              disabled={!canFilter || (view.preset && dateRestriction && !isPresetAllowedForRestriction(view.preset, dateRestriction))}
              onClick={() => applyQuickView(view)}
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`filter-card gam-report-shell ${filtersOpen ? 'filter-card-open' : ''}`} ref={filterPanelRef}>
        <div className="filter-card-head filter-card-head-sticky">
          <button
            type="button"
            className="filter-card-title filter-card-toggle"
            onClick={toggleFiltersPanel}
            aria-expanded={filtersOpen}
          >
            Historical report {filtersOpen ? '▾' : '▸'}
          </button>
          <div className="filter-actions filter-actions--desktop">
            <button className="btn-generate" onClick={applyFilter} disabled={!canFilter || !canRunReport}
              title={!canRunReport ? 'Select at least one dimension, metric, or inventory filter' : (canFilter ? '' : 'You do not have permission to apply filters')}>✓ Apply Filter</button>
            <SavedFiltersBar
              page={SAVED_FILTERS_PAGES.reporting}
              userId={user?.id}
              getSnapshot={getSavedFilterSnapshot}
              onApply={handleApplySavedFilter}
              canSave={canFilter}
              disabled={!canFilter}
            />
            {canDownload && reportReady && (
              <>
                <button className="btn-csv" onClick={downloadCSV} disabled={!displayRows.length}>⬇ CSV</button>
                <button className="btn-csv" onClick={downloadXls} disabled={!displayRows.length}>Excel</button>
              </>
            )}
            <button className="btn-reset" onClick={reset} disabled={!canFilter}>↺ Reset</button>
          </div>
        </div>

        <ReportSettingsFilters
          settings={reportSettings}
          onChange={setReportSettings}
          preset={preset}
          onPresetChange={applyPreset}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={(v) => { setStartDate(clampDateValue(v, dateRestriction)); setPreset('custom'); }}
          onEndDateChange={(v) => { setEndDate(clampDateValue(v, dateRestriction)); setPreset('custom'); }}
          disabled={!canFilter || dateFilterLocked}
          dateRestriction={dateRestriction}
        />
        {!dateFilterLocked && preset === 'custom' && (
          <div className="custom-range-hint">
            Pick <strong>start</strong> and <strong>end</strong> dates, then click <strong>Apply Filter</strong> to load data.
          </div>
        )}

        {allAppliedChips.length > 0 && (
          <FilterChips
            chips={allAppliedChips}
            expanded={chipsExpanded}
            onToggleExpand={toggleChipsExpanded}
            onAddFilter={canFilter ? handleAddFilter : undefined}
            onRemove={canFilter ? handleRemoveChip : undefined}
            title="Applied filters"
          />
        )}

        {recentFilters.length > 0 && (
          <div className="dash-breakdown-section gam-report-breakdown-section" style={{ marginTop: 8 }}>
            <div className="filter-section-divider" />
            <div className="filter-section-head" style={{ marginBottom: 8 }}>
              <span className="filter-section-title">Recently used filters</span>
              <span className="filter-section-hint">Reuse one of your recent filter sets</span>
            </div>
            <div className="preset-pills dash-preset-row">
              {recentFilters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="preset-pill"
                  onClick={() => {
                    const snapshot = applyRecentFilter(item.snapshot);
                    if (snapshot.startDate) setStartDate(snapshot.startDate);
                    if (snapshot.endDate) setEndDate(snapshot.endDate);
                    if (snapshot.preset) setPreset(snapshot.preset);
                    if (snapshot.country) setCountry(normalizeCountry(snapshot.country));
                    if (snapshot.domain) setDomain(snapshot.domain);
                    if (snapshot.site) setSite(snapshot.site);
                    if (snapshot.domainName) setDomainName(snapshot.domainName);
                    if (snapshot.domainId) setDomainId(snapshot.domainId);
                    if (snapshot.reportDimensions) setReportDimensions(snapshot.reportDimensions);
                    if (snapshot.reportMetrics) setReportMetrics(snapshot.reportMetrics);
                    if (snapshot.reportSettings) setReportSettings(snapshot.reportSettings);
                    setApplied({ ...applied, ...snapshot, startDate: snapshot.startDate || startDate, endDate: snapshot.endDate || endDate });
                    setHasApplied(true);
                  }}
                >{item.label}</button>
              ))}
            </div>
          </div>
        )}

        <GamReportControlBar
          showWarning={!canRunReport}
          onOpenBuilder={scrollToReportBuilder}
        />

        {filtersOpen && canReportBuilder && (
          <div className="gam-report-builder-section">
            <div className="filter-section-divider" />
            <ReportBuilderFilters
              dimensions={reportDimensions}
              metrics={reportMetrics}
              onDimensionsChange={setReportDimensions}
              onMetricsChange={setReportMetrics}
              disabled={!canFilter}
            />
          </div>
        )}

        {filtersOpen && breakdownOpen && (
          <div className="gam-report-breakdown-section">
            <div className="filter-section-divider" />
            {showNoDomainsNote ? (
              <NoDomainsAssignedNote />
            ) : (
            <>
            <div className="filter-section-head" style={{ marginBottom: 12 }}>
              <span className="filter-section-title">Breakdown filters</span>
              <span className="filter-section-hint">
                {filterVisibility.isScopedUser
                  ? 'Pick from your assigned list'
                  : 'Country, domain, site, ad unit & app filters'}
                {catalogBusy ? ' · Loading options…' : ''}
              </span>
            </div>
            <div className="filter-grid">
              {!filterVisibility.isScopedUser && (
              <div className="filter-field">
                <label>Country</label>
                <MultiSelect
                  options={countryMultiOptions}
                  value={country}
                  onChange={setCountry}
                  placeholder="Select countries"
                  disabled={!canFilter}
                  loading={countryLoading}
                  selectAllLabel="Select All Countries"
                />
              </div>
              )}
              {filterVisibility.showDomain && (
              <div className="filter-field">
                <label>Domain name</label>
                <MultiSelect options={domainRootOptions} value={domain} onChange={handleDomainChange}
                  placeholder="Select domain names" disabled={!canFilter} loading={catalogBusy} />
              </div>
              )}
              {filterVisibility.showSite && (
              <div className="filter-field">
                <label>Site (URL)</label>
                <MultiSelect options={siteOptions} value={site} onChange={handleSiteChange}
                  placeholder="Select sites" disabled={!canFilter} loading={catalogBusy} />
              </div>
              )}
              {filterVisibility.showAdUnit && (
              <div className="filter-field">
                <label>Ad Unit</label>
                <MultiSelect options={adUnitOptions} value={domainName} onChange={handleAdUnitChange}
                  placeholder="Select Ad Units" disabled={!canFilter} loading={catalogBusy} />
              </div>
              )}
              {filterVisibility.showApp && (
              <div className="filter-field">
                <label>App ID</label>
                <MultiSelect options={appOptions} value={domainId} onChange={handleAppChange}
                  placeholder="Select app IDs" disabled={!canFilter} loading={catalogBusy} />
              </div>
              )}
            </div>
            </>
            )}
          </div>
        )}
        {filtersOpen && canFilter && (
          <div className="filter-actions-foot">
            <button className="btn-generate" onClick={applyFilter} disabled={!canRunReport}
              title={!canRunReport ? 'Select at least one dimension, metric, or inventory filter' : ''}>
              ✓ Apply Filter
            </button>
            <button className="btn-reset" onClick={reset}>↺ Reset</button>
          </div>
        )}
        {!canFilter && (
          <p className="filter-locked-note">Filters are disabled for your account.</p>
        )}
      </div>

      {((loading && slowLoad) || showBuildingBanner) && (
        <div className="gam-report-warning" role="status">
          <span className="gam-report-warning-icon" aria-hidden>⏳</span>
          {showBuildingBanner
            ? 'Report is being prepared in the background. Results will appear shortly…'
            : 'Reports are taking longer than usual to respond. Please wait…'}
        </div>
      )}

      {error && (
        <div className="error-box dash-error-box" role="alert">
          <div className="dash-error-copy">
            <strong>Couldn’t load report</strong>
            <span>{error}</span>
          </div>
          <button type="button" onClick={() => load()} className="btn-retry">Retry</button>
        </div>
      )}

      {showSummaryCards && (
      <div className={`report-summary-row${summaryLoading ? ' is-loading' : ''}`}>
        {vis.revenue && (
          <div className={`report-sum-card${summaryLoading ? ' is-loading' : ''}`}>
            <span className="rsc-icon blue">💳</span>
            <div>
              <div className="rsc-label">Total Revenue</div>
              <div className="rsc-value">{summaryLoading ? <span className="card-spinner card-spinner-lg" aria-label="Loading" /> : money(summary.totalRevenue, currency)}</div>
            </div>
          </div>
        )}
        <div className={`report-sum-card${summaryLoading ? ' is-loading' : ''}`}>
          <span className="rsc-icon green">🌐</span>
          <div>
            <div className="rsc-label">Total App &amp; Website Domain</div>
            <div className="rsc-value">{summaryLoading ? <span className="card-spinner card-spinner-lg" aria-label="Loading" /> : num(summary.totalDomains)}</div>
          </div>
        </div>
        <div className={`report-sum-card${summaryLoading ? ' is-loading' : ''}`}>
          <span className="rsc-icon amber">📄</span>
          <div>
            <div className="rsc-label">Offered Records</div>
            <div className="rsc-value">{summaryLoading ? <span className="card-spinner card-spinner-lg" aria-label="Loading" /> : num(summary.offeredRecords)}</div>
          </div>
        </div>
        <div className="report-live">
          <span className="dot-pulse" /> {data?.isMock ? 'Mock' : 'Live'}
          <DataFreshness
            networkInfo={networkInfo}
            fetchedAt={lastUpdated}
            className="report-updated"
          />
        </div>
      </div>
      )}

      {showPartialCompatWarning && (
        <div className="warn-card warn-card-partial" role="status">
          <div className="warn-card-main">
            <div className="warn-card-left">
              <div className="warn-card-icon-wrap">
                <span aria-hidden>i</span>
              </div>
              <div className="warn-card-body">
                <div className="warn-card-title">Showing compatible data</div>
                <div className="warn-card-desc">
                  {Array.isArray(data?.reportWarningSubstitutions) && data.reportWarningSubstitutions.length
                    ? 'Some selected metrics can’t be combined exactly as in Google Ad Manager for this dimension mix. AdNexus mapped them to the closest warehouse totals so you still get a country/site/app breakdown.'
                    : 'Some selected dimensions or metrics can\'t be combined in one GAM report. Results below use the compatible subset. Remove the unavailable items for a complete selection.'}
                </div>
                {canFilter && skippedChips.some((c) => !String(c).includes('→')) && (
                  <div className="warn-card-btns">
                    <button type="button" className="warn-btn-primary" onClick={clearIncompatibleReporting}>
                      Remove these and apply
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="warn-card-right">
              <div className="warn-card-section-label">
                {Array.isArray(data?.reportWarningSubstitutions) && data.reportWarningSubstitutions.length
                  ? 'Mapped metrics'
                  : 'Skipped (incompatible)'}
              </div>
              <div className="warn-chip-row">
                {skippedChips.map((name) => (
                  <span key={name} className="warn-chip warn-chip-unavail">{name}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showNoReportCard && (
        <div className="warn-card" role="status">
          <div className="warn-card-main">
            <div className="warn-card-left">
              <div className="warn-card-icon-wrap">
                <span aria-hidden>i</span>
              </div>
              <div className="warn-card-body">
                <div className="warn-card-title">
                  {isEmptyResult ? 'No data for these filters' : 'No report data found'}
                </div>
                <div className="warn-card-desc">
                  {isEmptyResult
                    ? 'The report finished, but this combination of filters, dimensions and date range returned no records. Try a wider date range, fewer breakdown filters, or run it again in a few minutes if today’s data is still coming in.'
                    : 'The highlighted dimensions on the right could not return any data for your selected filters and date range. These dimensions are not supported in the current combination — remove them or adjust your selection to view complete data.'}
                </div>
                <div className="warn-card-btns">
                  {canFilter && (
                    <>
                      {isEmptyResult ? (
                        <button type="button" className="warn-btn-primary" onClick={() => load()}>
                          ↻ Run again
                        </button>
                      ) : (
                        <button type="button" className="warn-btn-primary" onClick={clearIncompatibleReporting}>
                          Remove these and apply
                        </button>
                      )}
                      <button type="button" className="warn-btn-secondary" onClick={reset}>↺ Reset Filters</button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {!isEmptyResult && (
              <div className="warn-card-right">
                <div className="warn-card-section-label">Unavailable dimensions for current selection</div>
                <div className="warn-chip-row">
                  {unavailableChips.map((name) => (
                    <span key={name} className="warn-chip warn-chip-unavail">{name}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="warn-card-hint-bar">
            <span className="warn-card-hint-icon" aria-hidden>i</span>
            <span>
              {isEmptyResult
                ? 'Reports for the current day fill in as Google Ad Manager delivers data, so a fresh run can return rows later.'
                : 'Some selected filters and metrics can’t be combined in the same report. Remove incompatible filters to view complete data.'}
            </span>
          </div>
        </div>
      )}

      {reportReady && (loading || hasReportData) && (
      <>
      {segmentFilter && (
        <div className="segment-filter-bar" role="status">
          <span className="segment-filter-chip">
            Filtered from chart: {dimensionLabel(segmentFilter.dimensionId)} = {segmentFilter.value}
            {displayRows.length !== tableRows.length
              ? ` · ${displayRows.length.toLocaleString()} of ${tableRows.length.toLocaleString()} rows`
              : ''}
          </span>
          <button type="button" className="segment-filter-clear" onClick={() => setSegmentFilter(null)}>Clear</button>
        </div>
      )}
      <DynamicReportTable
        title={tableConfig.mode === 'programmatic' ? 'Programmatic Channel Report' : 'Report Data'}
        rows={displayRows}
        dimensions={tableConfig.dimensions}
        metrics={tableConfig.metrics}
        visibility={vis}
        currency={currency}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        onPageReset={() => setPage(1)}
        searchPlaceholder={
          tableConfig.mode === 'programmatic'
            ? 'Search programmatic channel…'
            : 'Search date / domain / site / ad unit…'
        }
        page={page}
        pageSize={isNarrow ? 12 : PAGE_SIZE}
        onPageChange={setPage}
        pagination={data?.pagination || progData?.pagination || null}
        showTotals={reportReady && displayRows.length > 0}
        className="reporting-table"
        density={tableDensity}
        freezeFirst
        headerExtra={(
          <div className="multi-network-table-tools">
            <div className="table-density-toggle" role="group" aria-label="Table density">
              <button
                type="button"
                className={`table-density-btn${tableDensity === 'compact' ? ' active' : ''}`}
                onClick={() => setTableDensity('compact')}
              >
                Compact
              </button>
              <button
                type="button"
                className={`table-density-btn${tableDensity === 'comfortable' ? ' active' : ''}`}
                onClick={() => setTableDensity('comfortable')}
              >
                Comfortable
              </button>
            </div>
            {applied.startDate && applied.endDate
              ? <span className="report-range">{applied.startDate} → {applied.endDate}</span>
              : null}
          </div>
        )}
        noReportMessage="Select at least one metric to run a report"
        emptyMessage={segmentFilter ? 'No rows match this chart segment' : 'No data available'}
        onReset={canFilter ? reset : undefined}
        emptyActions={canFilter ? (
          <>
            <button type="button" className="btn-generate" onClick={() => applyPreset('yesterday')}>Try yesterday</button>
            <button type="button" className="btn-reset" onClick={() => applyPreset('last7')}>Try last 7 days</button>
            {segmentFilter && (
              <button type="button" className="btn-reset" onClick={() => setSegmentFilter(null)}>Clear chart filter</button>
            )}
          </>
        ) : null}
        columnStorageKey="reporting-table"
        canDownload={canDownload}
        exportName={`report_${applied.startDate || 'start'}_${applied.endDate || 'end'}`}
        onExportAll={fetchAllRowsForExport}
      />
      </>
      )}

      {/* Auto charts — preferred visualization(s) from selected dims/metrics */}
      {reportReady && hasReportData && (
        <ReportAutoCharts
          rows={tableRows}
          trend={data?.trend || progData?.trend || []}
          dimensions={tableConfig.dimensions}
          metrics={tableConfig.metrics}
          visibility={vis}
          currency={currency}
          startDate={applied.startDate}
          endDate={applied.endDate}
          mode={tableConfig.mode}
          isNarrow={isNarrow}
          onSegmentClick={handleChartSegmentClick}
          activeSegment={segmentFilter}
        />
      )}
      {canFilter && (
        <>
          <button type="button" className="filter-add-fab" onClick={handleAddFilter} aria-label="Add filter">
            <span className="filter-add-icon" aria-hidden>+</span>
            Add filter
          </button>
          <div className="filter-actions-foot filter-actions-foot--mobile">
            <button
              className="btn-generate"
              onClick={applyFilter}
              disabled={!canRunReport}
              title={!canRunReport ? 'Select at least one dimension, metric, or inventory filter' : ''}
            >
              ✓ Apply Filter
            </button>
            <button type="button" className="btn-reset-link" onClick={reset}>Reset</button>
          </div>
        </>
      )}
    </div>
  );
}

const TZ_REASON = {
  unsupported_report: 'Other timezones apply to reports by date, domain, site or app with revenue, impressions and clicks. This report uses other fields',
  hourly_behind: 'the newest hours are still being fetched',
  no_hourly_data: 'hourly data is not available yet',
  before_hourly_data: 'hourly data does not reach back to these dates',
};

/** Says which timezone the report's days are in, and why the chosen one was not used when it could not be. */
function TimezoneNote({ info }) {
  if (!info?.tz) return null;
  if (info.applied === false) {
    return (
      <p className="form-note page-restriction-note tz-report-note is-warn" role="status">
        Showing days in the network timezone ({info.networkTz}), not {info.tz}: {TZ_REASON[info.reason] || 'the selected timezone could not be applied'}.
      </p>
    );
  }
  return (
    <p className="form-note page-restriction-note tz-report-note" role="status">
      Days follow {info.tz}{info.mixed && info.hourlyFrom ? `; days before ${info.hourlyFrom} stay in the network timezone (${info.networkTz})` : ''}.
    </p>
  );
}

/** Reporting with a timezone picker; each zone remounts the report so no numbers carry over. */
export default function Reporting() {
  const { tz, options, change } = useReportTimezone();
  return (
    <>
      {options.options.length > 1 && (
        <TimezoneSwitcher
          networkTz={options.networkTz}
          options={options.options}
          value={tz}
          hourlyFrom={options.hourlyFrom}
          stale={Boolean(options.stale)}
          onChange={change}
        />
      )}
      <ReportingView key={tz || (options.networkTz && options.networkTz !== APP_TIMEZONE ? options.networkTz : 'network')} />
    </>
  );
}
